/**
 * V2Board API 服务基础模块
 * 统一的 API 客户端，错误处理，请求/响应拦截
 */
import axios from 'axios'

// ============================================
// 配置缓存
// ============================================
let _cachedBaseUrl = null
let _configPromise = null

/**
 * 获取后端 baseUrl（带缓存）
 */
export async function getBaseUrl() {
  if (_cachedBaseUrl) return _cachedBaseUrl
  
  // 检查全局变量
  if (window._V2BOARD_BASEURL) {
    _cachedBaseUrl = window._V2BOARD_BASEURL
    return _cachedBaseUrl
  }
  
  // 避免重复请求
  if (!_configPromise) {
    _configPromise = fetch('/config.json')
      .then(res => res.json())
      .then(config => {
        _cachedBaseUrl = config.baseUrl
        return _cachedBaseUrl
      })
      .catch(() => {
        _configPromise = null
        throw new Error('无法获取后端配置')
      })
  }
  
  return _configPromise
}

/**
 * 清除配置缓存（用于切换环境）
 */
export function clearConfigCache() {
  _cachedBaseUrl = null
  _configPromise = null
}

// ============================================
// 错误类型定义
// ============================================
export class ApiError extends Error {
  constructor(message, code, status, data = null) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.data = data
  }
}

// 错误码映射
const ERROR_MESSAGES = {
  401: '认证失败，请重新登录',
  403: '没有权限执行此操作',
  404: '请求的资源不存在',
  422: '请求参数错误',
  429: '请求过于频繁，请稍后再试',
  500: '服务器内部错误',
  502: '网关错误',
  503: '服务暂时不可用',
}

// ============================================
// API 客户端类
// ============================================
export class ApiClient {
  constructor(baseUrl, token = null) {
    this.baseUrl = baseUrl
    this.token = token
    
    this.client = axios.create({
      baseURL: baseUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      }
    })
    
    // 请求拦截器
    this.client.interceptors.request.use(
      (config) => {
        if (this.token) {
          config.headers.Authorization = this.token
        }
        return config
      },
      (error) => Promise.reject(error)
    )
    
    // 响应拦截器
    this.client.interceptors.response.use(
      (response) => this._handleResponse(response),
      (error) => this._handleError(error)
    )
  }
  
  /**
   * 更新 token
   */
  setToken(token) {
    this.token = token
  }
  
  /**
   * 处理响应
   */
  _handleResponse(response) {
    const data = response.data
    
    // V2Board 标准响应格式: { data: ... }
    if (data && data.data !== undefined) {
      return data.data
    }
    
    // 直接返回数据（如数组）
    if (data) {
      return data
    }
    
    throw new ApiError('API 返回数据格式不正确', 'INVALID_RESPONSE', response.status)
  }
  
  /**
   * 处理错误
   */
  _handleError(error) {
    // 网络错误
    if (!error.response) {
      throw new ApiError(
        error.message || '网络连接失败，请检查网络设置',
        'NETWORK_ERROR',
        0
      )
    }
    
    const { status, data } = error.response
    
    // 认证失败，触发全局事件
    if (status === 401) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'))
    }
    
    // 获取错误消息
    const message = data?.message 
      || ERROR_MESSAGES[status] 
      || `请求失败 (${status})`
    
    throw new ApiError(message, data?.code || 'HTTP_ERROR', status, data)
  }
  
  /**
   * 通用请求方法
   */
  async request(method, url, data = null, config = {}) {
    return this.client.request({
      method,
      url,
      data,
      ...config,
    })
  }
  
  // 便捷方法
  get(url, config) {
    return this.request('GET', url, null, config)
  }
  
  post(url, data, config) {
    return this.request('POST', url, data, config)
  }
  
  put(url, data, config) {
    return this.request('PUT', url, data, config)
  }
  
  delete(url, config) {
    return this.request('DELETE', url, null, config)
  }
}

// ============================================
// 访客 API（无需登录）
// ============================================
class GuestApiClient {
  constructor() {
    this._client = null
    this._initPromise = null
  }
  
  async _ensureClient() {
    if (this._client) return this._client
    
    if (!this._initPromise) {
      this._initPromise = getBaseUrl().then(baseUrl => {
        this._client = new ApiClient(baseUrl)
        return this._client
      })
    }
    
    return this._initPromise
  }
  
  async get(url) {
    const client = await this._ensureClient()
    return client.get(url)
  }
  
  async post(url, data) {
    const client = await this._ensureClient()
    return client.post(url, data)
  }
}

// 导出单例
export const guestApi = new GuestApiClient()

// ============================================
// 用户 API 工厂
// ============================================
let _userApiInstance = null

/**
 * 创建或获取用户 API 实例
 */
export function createUserApi(baseUrl, token) {
  _userApiInstance = new ApiClient(baseUrl, token)
  return _userApiInstance
}

/**
 * 获取当前用户 API 实例
 */
export function getUserApi() {
  return _userApiInstance
}

/**
 * 清除用户 API 实例（登出时调用）
 */
export function clearUserApi() {
  _userApiInstance = null
}

// ============================================
// 兼容旧版 V2BoardAPI 类
// ============================================
export default class V2BoardAPI {
  constructor(baseUrl, token) {
    this.baseUrl = baseUrl
    this.token = token
    this.client = axios.create({
      baseURL: baseUrl,
      headers: {
        'Authorization': token,
        'Content-Type': 'application/json'
      }
    })
  }

  async call(endpoint, method = 'GET', data = null) {
    try {
      const response = await this.client.request({
        url: endpoint,
        method,
        data
      })

      if (response.data && response.data.data !== undefined) {
        return response.data.data
      }
      
      if (response.data) {
        return response.data
      }

      throw new Error('API 返回数据格式不正确')
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          window.dispatchEvent(new CustomEvent('auth:unauthorized'))
          throw new Error('认证失败，请重新登录')
        }
        throw new Error(error.response.data?.message || `HTTP 错误! 状态: ${error.response.status}`)
      }
      throw new Error(error.message || '网络请求失败')
    }
  }
}
